import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase/supabaseClient';

/**
 * Patient Analytics Dashboard
 * Key metrics for patient engagement and adherence tracking
 */
const AnalyticsDashboard = () => {
  const [analytics, setAnalytics] = useState({
    // Core metrics
    activeUsers: 0,
    totalSessions: 0,
    gamePopularity: [],
    sessionCompletionRate: 0,
    averageSessionDuration: 0,
    averageGameDuration: 0,
    totalReps: 0,
    
    // Key metrics
    weeklyAdherentPatients: 0,
    sevenDayAdherenceRate: 0,
    dropOffPoints: [],
    userSessions: [],
    individualUsers: [],
    
    loading: true,
    error: null
  });

  const [dateRange, setDateRange] = useState('90');
  const [selectedTab, setSelectedTab] = useState('overview');

  useEffect(() => {
    fetchAnalytics();
  }, [dateRange]);

  const fetchAnalytics = async () => {
    setAnalytics(prev => ({ ...prev, loading: true, error: null }));

    try {
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - parseInt(dateRange));

      // Fetch data
      const [
        sessionsData,
        gameData,
        dashboardData
      ] = await Promise.all([
        fetchSessionsData(startDate, endDate),
        fetchGameData(startDate, endDate),
        fetchDashboardData(startDate, endDate)
      ]);

      // Calculate core metrics
      const uniqueUsers = new Set(sessionsData.map(s => s.user_name));
      const activeUsers = uniqueUsers.size;
      const totalSessions = sessionsData.length;

      // Calculate quality sessions (users with >= 10 minutes total game time)
      const userGameTime = {};
      gameData.forEach(game => {
        if (game.game_duration_seconds && game.game_duration_seconds > 0) {
          if (!userGameTime[game.user_name]) {
            userGameTime[game.user_name] = 0;
          }
          userGameTime[game.user_name] += game.game_duration_seconds;
        }
      });
      
      const qualityUsers = Object.values(userGameTime).filter(totalTime => totalTime >= 600).length;
      const sessionCompletionRate = activeUsers > 0 ? 
        Math.round((qualityUsers / activeUsers) * 100) : 0;

      // Calculate average session duration (platform time)
      const sessionsWithDuration = sessionsData.filter(s => s.session_duration_seconds);
      const averageSessionDuration = sessionsWithDuration.length > 0 ?
        Math.round(sessionsWithDuration.reduce((sum, s) => sum + s.session_duration_seconds, 0) / sessionsWithDuration.length) : 0;

      // Calculate average game duration
      const completedGames = gameData.filter(g => 
        g.event_type === 'game_completed' && g.game_duration_seconds && g.game_duration_seconds > 0
      );
      const averageGameDuration = completedGames.length > 0 ?
        Math.round(completedGames.reduce((sum, g) => sum + g.game_duration_seconds, 0) / completedGames.length) : 0;

      // Calculate 7-day adherence rate
      const sevenDayAdherenceRate = await calculateSevenDayAdherence(uniqueUsers);
      
      // Calculate weekly adherent patients (users who completed >= 5 sessions this week)
      const weeklyAdherentPatients = await calculateWeeklyAdherentPatients(uniqueUsers);

      // Process game popularity
      const gamePopularity = processGamePopularity(gameData);

      // Calculate total reps
      const totalReps = gameData.reduce((sum, game) => sum + (game.reps_completed || 0), 0);

      // Analyze drop-off points
      const dropOffPoints = await analyzeDropOffPoints(sessionsData, gameData, dashboardData);

      // Get user sessions timeline
      const userSessions = await processUserSessions(sessionsData, gameData);

      // Get individual user insights
      const individualUsers = await processIndividualUsers(sessionsData, gameData);

      setAnalytics({
        activeUsers,
        totalSessions,
        gamePopularity,
        sessionCompletionRate,
        averageSessionDuration,
        averageGameDuration,
        totalReps,
        weeklyAdherentPatients,
        sevenDayAdherenceRate,
        dropOffPoints,
        userSessions,
        individualUsers,
        loading: false,
        error: null
      });

    } catch (error) {
      console.error('Analytics fetch error:', error);
      setAnalytics(prev => ({
        ...prev,
        loading: false,
        error: 'Failed to load analytics. Please check Supabase connection.'
      }));
    }
  };

  // Helper functions for data fetching
  const fetchSessionsData = async (startDate, endDate) => {
    const { data, error } = await supabase
      .from('user_sessions')
      .select('*')
      .gte('session_start', startDate.toISOString())
      .lte('session_start', endDate.toISOString())
      .order('session_start', { ascending: false });
    
    if (error) throw error;
    return data || [];
  };

  const fetchGameData = async (startDate, endDate) => {
    const { data, error } = await supabase
      .from('game_events')
      .select('*')
      .gte('timestamp', startDate.toISOString())
      .lte('timestamp', endDate.toISOString())
      .order('timestamp', { ascending: false });
    
    if (error) throw error;
    return data || [];
  };

  const fetchDashboardData = async (startDate, endDate) => {
    const { data, error } = await supabase
      .from('dashboard_events')
      .select('*')
      .gte('timestamp', startDate.toISOString())
      .lte('timestamp', endDate.toISOString())
      .order('timestamp', { ascending: false });
    
    if (error) throw error;
    return data || [];
  };

  // Calculation functions
  const calculateSevenDayAdherence = async (users) => {
    if (users.size === 0) return 0;
    
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    
    let adherentUsers = 0;
    
    for (const user of users) {
      const { data } = await supabase
        .from('user_sessions')
        .select('session_start')
        .eq('user_name', user)
        .gte('session_start', sevenDaysAgo.toISOString())
        .not('session_end', 'is', null);
      
      // Consider adherent if they have sessions on at least 5 different days
      const uniqueDays = new Set((data || []).map(s => 
        new Date(s.session_start).toDateString()
      ));
      
      if (uniqueDays.size >= 5) {
        adherentUsers++;
      }
    }
    
    return Math.round((adherentUsers / users.size) * 100);
  };

  const calculateWeeklyAdherentPatients = async (users) => {
    if (users.size === 0) return 0;
    
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
    
    let adherentCount = 0;
    
    for (const user of users) {
      const { data } = await supabase
        .from('user_sessions')
        .select('id')
        .eq('user_name', user)
        .gte('session_start', oneWeekAgo.toISOString())
        .not('session_end', 'is', null);
      
      if (data && data.length >= 5) {
        adherentCount++;
      }
    }
    
    return adherentCount;
  };

  const processGamePopularity = (gameData) => {
    const gameStats = gameData.reduce((acc, game) => {
      if (!acc[game.game_name]) {
        acc[game.game_name] = { 
          started: 0, 
          completed: 0, 
          abandoned: 0, 
          totalReps: 0, 
          repSessions: 0,
          totalDuration: 0,
          completedDuration: 0,
          completedWithDuration: 0
        };
      }
      
      if (game.event_type === 'game_started') {
        acc[game.game_name].started++;
      } else if (game.event_type === 'game_completed') {
        acc[game.game_name].completed++;
        if (game.game_duration_seconds && game.game_duration_seconds > 0) {
          acc[game.game_name].completedDuration += game.game_duration_seconds;
          acc[game.game_name].completedWithDuration++;
        }
      } else if (game.event_type === 'game_abandoned') {
        acc[game.game_name].abandoned++;
        if (game.game_duration_seconds && game.game_duration_seconds > 0) {
          acc[game.game_name].totalDuration += game.game_duration_seconds;
        }
      }
      
      if (game.reps_completed && game.reps_completed > 0) {
        acc[game.game_name].totalReps += game.reps_completed;
        acc[game.game_name].repSessions++;
      }
      
      return acc;
    }, {});

    return Object.entries(gameStats).map(([name, stats]) => {
      // Calculate rates based on actual game outcomes (completed + abandoned)
      const totalFinished = stats.completed + stats.abandoned;
      const completionRate = totalFinished > 0 ? Math.round((stats.completed / totalFinished) * 100) : 0;
      const abandonmentRate = totalFinished > 0 ? Math.round((stats.abandoned / totalFinished) * 100) : 0;
      
      // Calculate average duration from completed games only
      const avgDuration = stats.completedWithDuration > 0 ? 
        Math.round(stats.completedDuration / stats.completedWithDuration) : 0;
      
      return {
        name,
        timesPlayed: stats.started,
        finishedGames: totalFinished,
        completionRate,
        abandonmentRate,
        avgReps: stats.repSessions > 0 ? Math.round(stats.totalReps / stats.repSessions) : 0,
        totalReps: stats.totalReps,
        avgDuration,
        inProgress: Math.max(0, stats.started - totalFinished) // Games started but not finished
      };
    }).sort((a, b) => b.timesPlayed - a.timesPlayed);
  };

  const analyzeDropOffPoints = async (sessionsData, gameData, dashboardData) => {
    const dropOffPoints = [];
    
    if (sessionsData.length === 0) return dropOffPoints;
    
    // Get all unique users from sessions
    const allUsers = new Set(sessionsData.map(s => s.user_name));
    const totalUsers = allUsers.size;
    
    if (totalUsers === 0) return dropOffPoints;
    
    // 1. Never Return - Users with only one session (single visit drop-off)
    const userSessionCounts = {};
    sessionsData.forEach(session => {
      const userName = session.user_name;
      userSessionCounts[userName] = (userSessionCounts[userName] || 0) + 1;
    });
    
    const singleSessionUsers = Object.keys(userSessionCounts).filter(user => userSessionCounts[user] === 1).length;
    
    if (singleSessionUsers > 0) {
      dropOffPoints.push({
        point: 'Never Return',
        count: singleSessionUsers,
        percentage: Math.round((singleSessionUsers / totalUsers) * 100),
        description: 'Users who visit once and never return'
      });
    }
    
    // 2. Game Hesitant - Users who login but never start a game
    const usersWhoStartedGames = new Set(gameData.filter(g => g.event_type === 'game_started').map(g => g.user_name));
    const gameHesitantUsers = totalUsers - usersWhoStartedGames.size;
    
    if (gameHesitantUsers > 0) {
      dropOffPoints.push({
        point: 'Game Hesitant',
        count: gameHesitantUsers,
        percentage: Math.round((gameHesitantUsers / totalUsers) * 100),
        description: 'Users who login but never start a therapeutic game'
      });
    }
    
    // 3. First Game Drop-Off - Users who start games but never complete any
    const usersWhoCompletedGames = new Set(
      gameData.filter(g => g.event_type === 'game_completed').map(g => g.user_name)
    );
    
    const firstGameDropOff = usersWhoStartedGames.size - usersWhoCompletedGames.size;
    
    if (firstGameDropOff > 0 && usersWhoStartedGames.size > 0) {
      dropOffPoints.push({
        point: 'First Game Drop-Off',
        count: firstGameDropOff,
        percentage: Math.round((firstGameDropOff / usersWhoStartedGames.size) * 100),
        description: 'Users who start games but never complete their first one'
      });
    }
    
    // 4. Single Game Users - Users who complete exactly 1 game and never try others
    const userGameCompletions = {};
    gameData.filter(g => g.event_type === 'game_completed').forEach(game => {
      const userName = game.user_name;
      userGameCompletions[userName] = (userGameCompletions[userName] || 0) + 1;
    });
    
    const singleGameUsers = Object.keys(userGameCompletions).filter(user => userGameCompletions[user] === 1).length;
    
    if (singleGameUsers > 0 && usersWhoCompletedGames.size > 0) {
      dropOffPoints.push({
        point: 'Single Game Users',
        count: singleGameUsers,
        percentage: Math.round((singleGameUsers / usersWhoCompletedGames.size) * 100),
        description: 'Users who complete 1 game but never try others'
      });
    }
    
    // 5. Engagement Fade - Users who were active but haven't returned in 14+ days
    const fourteenDaysAgo = new Date();
    fourteenDaysAgo.setDate(fourteenDaysAgo.getDate() - 14);
    
    const recentlyActiveSessions = sessionsData.filter(s => 
      new Date(s.session_start) >= fourteenDaysAgo
    );
    const recentlyActiveUsers = new Set(recentlyActiveSessions.map(s => s.user_name));
    
    const fadedUsers = totalUsers - recentlyActiveUsers.size;
    
    if (fadedUsers > 0) {
      dropOffPoints.push({
        point: 'Engagement Fade',
        count: fadedUsers,
        percentage: Math.round((fadedUsers / totalUsers) * 100),
        description: 'Users who were active but haven\'t returned in 14+ days'
      });
    }
    
    return dropOffPoints;
  };

  const processUserSessions = async (sessionsData, gameData) => {
    return sessionsData.map(session => {
      const sessionGames = gameData.filter(g => g.session_id === session.id);
      const gamesPlayed = sessionGames.filter(g => g.event_type === 'game_started').length;
      const gamesCompleted = sessionGames.filter(g => g.event_type === 'game_completed').length;
      const totalReps = sessionGames.reduce((sum, g) => sum + (g.reps_completed || 0), 0);
      
      return {
        ...session,
        gamesPlayed,
        gamesCompleted,
        totalReps,
        completionRate: gamesPlayed > 0 ? Math.round((gamesCompleted / gamesPlayed) * 100) : 0
      };
    });
  };

  const processIndividualUsers = async (sessionsData, gameData) => {
    const userMap = new Map();
    
    // Process sessions for each user
    sessionsData.forEach(session => {
      if (!userMap.has(session.user_name)) {
        userMap.set(session.user_name, {
          userName: session.user_name,
          totalSessions: 0,
          completedSessions: 0,
          totalDuration: 0,
          totalReps: 0,
          games: {},
          firstSession: session.session_start,
          lastSession: session.session_start
        });
      }
      
      const user = userMap.get(session.user_name);
      user.totalSessions++;
      
      if (session.session_end) {
        user.completedSessions++;
        if (session.session_duration_seconds) {
          user.totalDuration += session.session_duration_seconds;
        }
      }
      
      // Track first and last session dates
      if (new Date(session.session_start) < new Date(user.firstSession)) {
        user.firstSession = session.session_start;
      }
      if (new Date(session.session_start) > new Date(user.lastSession)) {
        user.lastSession = session.session_start;
      }
    });
    
    // Process game data for each user
    gameData.forEach(game => {
      if (!userMap.has(game.user_name)) return;
      
      const user = userMap.get(game.user_name);
      
      if (!user.games[game.game_name]) {
        user.games[game.game_name] = {
          plays: 0,
          completed: 0,
          abandoned: 0,
          totalReps: 0,
          totalDuration: 0,
          completedDuration: 0
        };
      }
      
      const gameStats = user.games[game.game_name];
      
      if (game.event_type === 'game_started') {
        gameStats.plays++;
      } else if (game.event_type === 'game_completed') {
        gameStats.completed++;
        if (game.game_duration_seconds) {
          gameStats.completedDuration += game.game_duration_seconds;
          gameStats.totalDuration += game.game_duration_seconds;
        }
      } else if (game.event_type === 'game_abandoned') {
        gameStats.abandoned++;
        if (game.game_duration_seconds) {
          gameStats.totalDuration += game.game_duration_seconds;
        }
      }
      
      if (game.reps_completed) {
        gameStats.totalReps += game.reps_completed;
        user.totalReps += game.reps_completed;
      }
    });
    
    // Convert to array and calculate additional metrics
    return Array.from(userMap.values()).map(user => {
      const avgSessionDuration = user.completedSessions > 0 ? 
        Math.round(user.totalDuration / user.completedSessions) : 0;
      
      // Calculate quality game sessions (total game time ≥10 minutes)
      const gameEntries = Object.entries(user.games);
      const totalGameTime = gameEntries.reduce((sum, [, game]) => sum + game.totalDuration, 0);
      const hasQualityGameSession = totalGameTime >= 600; // 10 minutes in seconds
      const sessionCompletionRate = hasQualityGameSession ? 100 : 0;
      
      // Calculate favorite game (most plays)
      const favoriteGame = gameEntries.length > 0 ? 
        gameEntries.reduce((a, b) => a[1].plays > b[1].plays ? a : b)[0] : 'None';
      
      // Calculate total game completion rate (completed / (completed + abandoned))
      const totalGameCompletions = gameEntries.reduce((sum, [, game]) => sum + game.completed, 0);
      const totalGameAbandoned = gameEntries.reduce((sum, [, game]) => sum + game.abandoned, 0);
      const totalFinishedGames = totalGameCompletions + totalGameAbandoned;
      const gameCompletionRate = totalFinishedGames > 0 ? 
        Math.round((totalGameCompletions / totalFinishedGames) * 100) : 0;
      
      return {
        ...user,
        avgSessionDuration,
        sessionCompletionRate,
        favoriteGame,
        gameCompletionRate,
        gamesPlayed: gameEntries.length,
        daysSinceFirstSession: Math.ceil((new Date() - new Date(user.firstSession)) / (1000 * 60 * 60 * 24)),
        daysSinceLastSession: Math.ceil((new Date() - new Date(user.lastSession)) / (1000 * 60 * 60 * 24))
      };
    }).sort((a, b) => b.totalSessions - a.totalSessions); // Sort by most active users first
  };

  // Utility functions
  const formatDuration = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (analytics.loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <div>Loading analytics...</div>
      </div>
    );
  }

  if (analytics.error) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: 'red' }}>
        <div>{analytics.error}</div>
        <button onClick={fetchAnalytics} style={{ marginTop: '10px' }}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', fontFamily: 'Arial, sans-serif', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
        <h1>Patient Analytics Dashboard</h1>
        <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
          <label>Time Period: </label>
          <select 
            value={dateRange} 
            onChange={(e) => setDateRange(e.target.value)}
            style={{ padding: '5px' }}
          >
            <option value="1">Last 24 hours</option>
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
          </select>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div style={{ marginBottom: '30px', borderBottom: '2px solid #e0e0e0' }}>
        <div style={{ display: 'flex', gap: '30px' }}>
          {['overview', 'adherence', 'dropoff', 'users', 'individual'].map(tab => (
            <button
              key={tab}
              onClick={() => setSelectedTab(tab)}
              style={{
                padding: '10px 20px',
                border: 'none',
                background: selectedTab === tab ? '#3b82f6' : 'transparent',
                color: selectedTab === tab ? 'white' : '#666',
                cursor: 'pointer',
                borderRadius: '5px 5px 0 0',
                textTransform: 'capitalize',
                fontWeight: selectedTab === tab ? 'bold' : 'normal'
              }}
            >
              {tab === 'dropoff' ? 'Drop-off Analysis' : 
               tab === 'individual' ? 'Individual Users' : tab}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      {selectedTab === 'overview' && (
        <div>


          {/* Key Metrics Grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', marginBottom: '30px' }}>
            <div style={{ background: '#f0f9ff', padding: '20px', borderRadius: '8px', textAlign: 'center' }}>
              <h3 style={{ margin: '0 0 10px 0', color: '#0369a1' }}>Active Users</h3>
              <div style={{ fontSize: '48px', fontWeight: 'bold', color: '#0369a1', margin: '10px 0' }}>
                {analytics.activeUsers}
              </div>
              <div style={{ fontSize: '0.9em', color: '#666', marginTop: '5px' }}>
                Unique users in period
              </div>
            </div>

            <div style={{ background: '#f0fdf4', padding: '20px', borderRadius: '8px', textAlign: 'center' }}>
              <h3 style={{ margin: '0 0 10px 0', color: '#15803d' }}>7-Day Adherence Rate</h3>
              <div style={{ fontSize: '2em', fontWeight: 'bold', color: '#15803d' }}>
                {analytics.sevenDayAdherenceRate}%
              </div>
              <div style={{ fontSize: '0.9em', color: '#666', marginTop: '5px' }}>
                Users active ≥5 days/week
              </div>
            </div>

            <div style={{ background: '#fefce8', padding: '20px', borderRadius: '8px', textAlign: 'center' }}>
              <h3 style={{ margin: '0 0 10px 0', color: '#a16207' }}>Quality Game Sessions</h3>
              <div style={{ fontSize: '2em', fontWeight: 'bold', color: '#a16207' }}>
                {analytics.sessionCompletionRate}%
              </div>
              <div style={{ fontSize: '0.9em', color: '#666', marginTop: '5px' }}>
                Users with ≥10 min total game time
              </div>
            </div>

            <div style={{ background: '#fdf2f8', padding: '20px', borderRadius: '8px', textAlign: 'center' }}>
              <h3 style={{ margin: '0 0 10px 0', color: '#be185d' }}>Avg Platform Time</h3>
              <div style={{ fontSize: '1.5em', fontWeight: 'bold', color: '#be185d' }}>
                {formatDuration(analytics.averageSessionDuration)}
              </div>
              <div style={{ fontSize: '0.9em', color: '#666', marginTop: '5px' }}>
                Login to logout time
              </div>
            </div>

            <div style={{ background: '#f3f4f6', padding: '20px', borderRadius: '8px', textAlign: 'center' }}>
              <h3 style={{ margin: '0 0 10px 0', color: '#374151' }}>Total Reps</h3>
              <div style={{ fontSize: '2em', fontWeight: 'bold', color: '#374151' }}>
                {analytics.totalReps.toLocaleString()}
              </div>
              <div style={{ fontSize: '0.9em', color: '#666', marginTop: '5px' }}>
                Exercise repetitions
              </div>
            </div>

            <div style={{ background: '#fef3c7', padding: '20px', borderRadius: '8px', textAlign: 'center' }}>
              <h3 style={{ margin: '0 0 10px 0', color: '#d97706' }}>Total Sessions</h3>
              <div style={{ fontSize: '2em', fontWeight: 'bold', color: '#d97706' }}>
                {analytics.totalSessions}
              </div>
              <div style={{ fontSize: '0.9em', color: '#666', marginTop: '5px' }}>
                Login sessions
              </div>
            </div>


          </div>

          {/* Game Performance */}
          <div style={{ background: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
            <h2 style={{ marginTop: 0 }}>Game Performance</h2>
            {analytics.gamePopularity.length > 0 ? (
              <div>
                {analytics.gamePopularity.map((game, index) => (
                  <div key={game.name} style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    padding: '20px 0',
                    borderBottom: index < analytics.gamePopularity.length - 1 ? '1px solid #f0f0f0' : 'none'
                  }}>
                    <div style={{ flex: 1 }}>
                      <strong style={{ fontSize: '1.1em' }}>{game.name}</strong>
                      <div style={{ fontSize: '0.9em', color: '#666', marginTop: '5px' }}>
                        {game.timesPlayed} plays • {game.finishedGames} finished • {game.totalReps} total reps
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '25px', alignItems: 'center' }}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '1.2em', fontWeight: 'bold', color: '#15803d' }}>
                          {game.avgReps}
                        </div>
                        <div style={{ fontSize: '0.8em', color: '#666' }}>
                          avg reps
                        </div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '1.2em', fontWeight: 'bold', color: '#3b82f6' }}>
                          {game.completionRate}%
                        </div>
                        <div style={{ fontSize: '0.8em', color: '#666' }}>
                          completion
                        </div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '1.2em', fontWeight: 'bold', color: '#dc2626' }}>
                          {game.abandonmentRate}%
                        </div>
                        <div style={{ fontSize: '0.8em', color: '#666' }}>
                          abandonment
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ color: '#666', fontStyle: 'italic' }}>
                No game data available for this period
              </div>
            )}
          </div>
        </div>
      )}

      {selectedTab === 'adherence' && (
        <div>
          <h2>Adherence Analysis</h2>
          
          {/* Adherence Metrics */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px', marginBottom: '30px' }}>
            <div style={{ background: '#ecfdf5', padding: '25px', borderRadius: '8px', border: '2px solid #10b981' }}>
              <h3 style={{ margin: '0 0 15px 0', color: '#065f46' }}>7-Day Adherence Rate</h3>
              <div style={{ fontSize: '3em', fontWeight: 'bold', color: '#065f46', marginBottom: '10px' }}>
                {analytics.sevenDayAdherenceRate}%
              </div>
              <p style={{ margin: '0', color: '#047857' }}>
                Percentage of users completing full week of prescribed exercises (≥5 days)
              </p>
            </div>

            <div style={{ background: '#fef3c7', padding: '25px', borderRadius: '8px', border: '2px solid #f59e0b' }}>
              <h3 style={{ margin: '0 0 15px 0', color: '#92400e' }}>Weekly Adherent Patients</h3>
              <div style={{ fontSize: '3em', fontWeight: 'bold', color: '#92400e', marginBottom: '10px' }}>
                {analytics.weeklyAdherentPatients}
              </div>
              <p style={{ margin: '0', color: '#d97706' }}>
                Number of patients meeting weekly exercise goals
              </p>
            </div>
          </div>
        </div>
      )}

      {selectedTab === 'dropoff' && (
        <div>
          <h2>Drop-off Point Analysis</h2>
          
          {analytics.dropOffPoints.length > 0 ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
              {analytics.dropOffPoints.map((point, index) => (
                <div key={index} style={{ 
                  background: 'white', 
                  padding: '20px', 
                  borderRadius: '8px', 
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  border: '2px solid #fecaca'
                }}>
                  <h3 style={{ margin: '0 0 15px 0', color: '#dc2626' }}>{point.point}</h3>
                  <div style={{ fontSize: '2.5em', fontWeight: 'bold', color: '#dc2626', marginBottom: '10px' }}>
                    {point.percentage}%
                  </div>
                  <div style={{ fontSize: '1.1em', color: '#666', marginBottom: '10px' }}>
                    {point.count} users affected
                  </div>
                  <p style={{ margin: '0', color: '#4b5563', fontSize: '0.9em' }}>
                    {point.description}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ 
              background: '#f0f9ff', 
              padding: '40px', 
              borderRadius: '8px', 
              textAlign: 'center',
              color: '#0369a1'
            }}>
              <h3>No significant drop-off points detected</h3>
              <p>Users are completing sessions and games successfully.</p>
            </div>
          )}
        </div>
      )}

      {selectedTab === 'users' && (
        <div>
          <h2>User Session Timeline</h2>
          
          <div style={{ background: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
            <div style={{ marginBottom: '20px', fontSize: '0.9em', color: '#666' }}>
              Showing {analytics.userSessions.length} most recent sessions
            </div>
            
            {analytics.userSessions.length > 0 ? (
              <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
                {analytics.userSessions.map((session, index) => (
                  <div key={session.id} style={{ 
                    padding: '15px 0',
                    borderBottom: index < analytics.userSessions.length - 1 ? '1px solid #f0f0f0' : 'none'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                          <strong style={{ color: '#1f2937' }}>{session.user_name}</strong>
                          <span style={{ 
                            background: session.session_end ? '#dcfce7' : '#fef3c7',
                            color: session.session_end ? '#15803d' : '#d97706',
                            padding: '2px 8px',
                            borderRadius: '12px',
                            fontSize: '0.8em'
                          }}>
                            {session.session_end ? 'Completed' : 'In Progress'}
                          </span>
                        </div>
                        
                        <div style={{ fontSize: '0.9em', color: '#666', marginBottom: '8px' }}>
                          {formatDate(session.session_start)}
                          {session.session_end && (
                            <span> • Duration: {formatDuration(session.session_duration_seconds || 0)}</span>
                          )}
                        </div>
                        
                        <div style={{ fontSize: '0.9em', color: '#4b5563' }}>
                          {session.gamesPlayed} games played • {session.gamesCompleted} completed • {session.totalReps} reps
                        </div>
                      </div>
                      
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ 
                          fontSize: '1.2em', 
                          fontWeight: 'bold', 
                          color: session.completionRate >= 80 ? '#15803d' : session.completionRate >= 50 ? '#d97706' : '#dc2626'
                        }}>
                          {session.completionRate}%
                        </div>
                        <div style={{ fontSize: '0.8em', color: '#666' }}>
                          completion rate
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ color: '#666', fontStyle: 'italic', textAlign: 'center', padding: '40px' }}>
                No user sessions found for this period
              </div>
            )}
          </div>
        </div>
      )}

      {selectedTab === 'individual' && (
        <div>
          <h2>Individual User Insights</h2>
          
          {/* User Engagement Summary */}
          <div style={{ background: '#f8fafc', padding: '20px', borderRadius: '8px', marginBottom: '20px' }}>
            <h3 style={{ margin: '0 0 15px 0', color: '#334155' }}>User Engagement Summary</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '15px' }}>
              <div style={{ textAlign: 'center', background: 'white', padding: '15px', borderRadius: '6px' }}>
                <div style={{ fontSize: '1.8em', fontWeight: 'bold', color: '#dc2626' }}>
                  {analytics.individualUsers ? Math.round(analytics.individualUsers.reduce((sum, u) => sum + u.totalReps, 0) / analytics.individualUsers.length) : 0}
                </div>
                <div style={{ fontSize: '0.85em', color: '#666', marginTop: '5px' }}>Avg Reps per User</div>
                <div style={{ fontSize: '0.75em', color: '#999' }}>Therapeutic repetitions</div>
              </div>
              <div style={{ textAlign: 'center', background: 'white', padding: '15px', borderRadius: '6px' }}>
                <div style={{ fontSize: '1.8em', fontWeight: 'bold', color: '#7c3aed' }}>
                  {analytics.individualUsers ? analytics.individualUsers.filter(u => u.gameCompletionRate > 50).length : 0}
                </div>
                <div style={{ fontSize: '0.85em', color: '#666', marginTop: '5px' }}>High Completion</div>
                <div style={{ fontSize: '0.75em', color: '#999' }}>&gt;50% game completion</div>
              </div>
              <div style={{ textAlign: 'center', background: 'white', padding: '15px', borderRadius: '6px' }}>
                <div style={{ fontSize: '1.8em', fontWeight: 'bold', color: '#ea580c' }}>
                  {analytics.individualUsers ? analytics.individualUsers.filter(u => u.daysSinceLastSession <= 7).length : 0}
                </div>
                <div style={{ fontSize: '0.85em', color: '#666', marginTop: '5px' }}>Recently Active</div>
                <div style={{ fontSize: '0.75em', color: '#999' }}>≤7 days ago</div>
              </div>
              <div style={{ textAlign: 'center', background: 'white', padding: '15px', borderRadius: '6px' }}>
                <div style={{ fontSize: '1.8em', fontWeight: 'bold', color: '#0891b2' }}>
                  {analytics.individualUsers ? analytics.individualUsers.filter(u => u.totalReps > 100).length : 0}
                </div>
                <div style={{ fontSize: '0.85em', color: '#666', marginTop: '5px' }}>Power Users</div>
                <div style={{ fontSize: '0.75em', color: '#999' }}>&gt;100 total reps</div>
              </div>
            </div>
          </div>
          
          <div style={{ background: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
            <div style={{ marginBottom: '20px', fontSize: '0.9em', color: '#666' }}>
              Showing detailed insights for {analytics.individualUsers.length} users
            </div>
            
            {analytics.individualUsers.length > 0 ? (
              <div>
                {analytics.individualUsers.map((user, index) => (
                  <div key={user.userName} style={{ 
                    marginBottom: '30px',
                    padding: '20px',
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    background: '#fafafa'
                  }}>
                    {/* User Header */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
                      <div>
                        <h3 style={{ margin: '0 0 10px 0', color: '#1f2937', fontSize: '1.3em' }}>
                          {user.userName}
                        </h3>
                        <div style={{ fontSize: '0.9em', color: '#666' }}>
                          Active for {user.daysSinceFirstSession} days • Last seen {user.daysSinceLastSession} days ago
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '1.5em', fontWeight: 'bold', color: '#3b82f6' }}>
                          {user.totalReps}
                        </div>
                        <div style={{ fontSize: '0.8em', color: '#666' }}>
                          total reps
                        </div>
                      </div>
                    </div>

                    {/* Key Engagement Metrics */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '15px', marginBottom: '20px' }}>
                      <div style={{ textAlign: 'center', padding: '15px', background: 'white', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                        <div style={{ fontSize: '1.4em', fontWeight: 'bold', color: user.sessionCompletionRate > 0 ? '#059669' : '#6b7280' }}>
                          {user.sessionCompletionRate > 0 ? 'YES' : 'NO'}
                        </div>
                        <div style={{ fontSize: '0.85em', color: '#666', marginTop: '4px' }}>
                          Quality Game Sessions
                        </div>
                        <div style={{ fontSize: '0.75em', color: '#999' }}>
                          ≥10 min total game time
                        </div>
                      </div>
                      
                      <div style={{ textAlign: 'center', padding: '15px', background: 'white', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                        <div style={{ fontSize: '1.4em', fontWeight: 'bold', color: '#dc2626' }}>
                          {user.totalGamePlays || 0}
                        </div>
                        <div style={{ fontSize: '0.85em', color: '#666', marginTop: '4px' }}>
                          Total Game Plays
                        </div>
                        <div style={{ fontSize: '0.75em', color: '#999' }}>
                          All game attempts
                        </div>
                      </div>
                      
                      <div style={{ textAlign: 'center', padding: '15px', background: 'white', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                        <div style={{ fontSize: '1.4em', fontWeight: 'bold', color: '#7c3aed' }}>
                          {user.gameCompletionRate}%
                        </div>
                        <div style={{ fontSize: '0.85em', color: '#666', marginTop: '4px' }}>
                          Game Completion
                        </div>
                        <div style={{ fontSize: '0.75em', color: '#999' }}>
                          Finished vs abandoned
                        </div>
                      </div>
                      
                      <div style={{ textAlign: 'center', padding: '15px', background: 'white', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                        <div style={{ fontSize: '1.4em', fontWeight: 'bold', color: '#ea580c' }}>
                          {user.daysSinceLastSession}
                        </div>
                        <div style={{ fontSize: '0.85em', color: '#666', marginTop: '4px' }}>
                          Days Since Last
                        </div>
                        <div style={{ fontSize: '0.75em', color: '#999' }}>
                          Recent activity
                        </div>
                      </div>
                      
                      <div style={{ textAlign: 'center', padding: '15px', background: 'white', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                        <div style={{ fontSize: '1.2em', fontWeight: 'bold', color: '#374151', wordBreak: 'break-word' }}>
                          {user.favoriteGame || 'None'}
                        </div>
                        <div style={{ fontSize: '0.85em', color: '#666', marginTop: '4px' }}>
                          Most Played Game
                        </div>
                        <div style={{ fontSize: '0.75em', color: '#999' }}>
                          By total plays
                        </div>
                      </div>
                    </div>

                    {/* Game Performance */}
                    <div>
                      <h4 style={{ margin: '0 0 15px 0', color: '#374151' }}>Game Performance</h4>
                      {Object.keys(user.games).length > 0 ? (
                        <div style={{ display: 'grid', gap: '12px' }}>
                          {Object.entries(user.games).map(([gameName, gameStats]) => {
                            const finishedGames = (gameStats.completed || 0) + (gameStats.abandoned || 0);
                            const completionRate = finishedGames > 0 ? 
                              Math.round(((gameStats.completed || 0) / finishedGames) * 100) : 0;
                            const abandonmentRate = finishedGames > 0 ? 
                              Math.round(((gameStats.abandoned || 0) / finishedGames) * 100) : 0;
                            const avgDuration = (gameStats.completed || 0) > 0 ? 
                              Math.round((gameStats.totalDuration || 0) / (gameStats.completed || 1)) : 0;
                            const avgReps = finishedGames > 0 ? 
                              Math.round((gameStats.totalReps || 0) / finishedGames) : 0;
                            
                            return (
                              <div key={gameName} style={{ 
                                display: 'flex', 
                                justifyContent: 'space-between', 
                                alignItems: 'center',
                                padding: '15px',
                                background: 'white',
                                borderRadius: '8px',
                                border: '1px solid #e5e7eb'
                              }}>
                                <div style={{ flex: '1' }}>
                                  <strong style={{ color: '#1f2937', fontSize: '1.1em' }}>{gameName}</strong>
                                  <div style={{ fontSize: '0.85em', color: '#666', marginTop: '4px' }}>
                                    {gameStats.plays || 0} plays • {finishedGames} finished • {gameStats.totalReps || 0} total reps
                                  </div>
                                </div>
                                <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
                                  <div style={{ textAlign: 'center' }}>
                                    <div style={{ fontSize: '1.2em', fontWeight: 'bold', color: '#15803d' }}>
                                      {avgReps}
                                    </div>
                                    <div style={{ fontSize: '0.75em', color: '#666' }}>
                                      avg reps
                                    </div>
                                  </div>
                                  <div style={{ textAlign: 'center' }}>
                                    <div style={{ fontSize: '1.2em', fontWeight: 'bold', color: '#3b82f6' }}>
                                      {completionRate}%
                                    </div>
                                    <div style={{ fontSize: '0.75em', color: '#666' }}>
                                      completion
                                    </div>
                                  </div>
                                  <div style={{ textAlign: 'center' }}>
                                    <div style={{ fontSize: '1.2em', fontWeight: 'bold', color: '#dc2626' }}>
                                      {abandonmentRate}%
                                    </div>
                                    <div style={{ fontSize: '0.75em', color: '#666' }}>
                                      abandonment
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div style={{ color: '#666', fontStyle: 'italic', padding: '20px', textAlign: 'center' }}>
                          No game activity recorded
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ color: '#666', fontStyle: 'italic', textAlign: 'center', padding: '40px' }}>
                No user data available for this period
              </div>
            )}
          </div>
        </div>
      )}

      {/* Refresh Button */}
      <div style={{ textAlign: 'center', marginTop: '40px' }}>
        <button 
          onClick={fetchAnalytics}
          style={{
            background: '#3b82f6',
            color: 'white',
            border: 'none',
            padding: '12px 24px',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '1em',
            fontWeight: 'bold'
          }}
        >
          Refresh All Data
        </button>
      </div>
    </div>
  );
};

export default AnalyticsDashboard; 